import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { first } from 'rxjs/operators'
import { BsModalService } from "ngx-bootstrap/modal";
import { BaseViewComponent } from '../../../../../../shared/core/interfaces/baseViewComponent';
import { LookupService } from '../../../../../../shared/core/services/lookup.service';
import { ToastService } from '../../../../../../shared/core/services/toast.service';
import { GeographyService } from '../../../../../../shared/core/services/geography.service';
import { PostalCodePipe } from '../../../../../../shared/ui-ndms/pipes/postal-code.pipe';
import { SearchAirportsModalComponent } from '../../../../responders/shared/responder-profile/travel-info/airports/search/search-airports-modal.component';
import { ResponderCurrentAttendanceService } from '../../../../api/services/responder-current-attendance.service';
import { EsriMarkerData } from './../../../../../../shared/ui-ndms/components/map-esri/map-esri.component';

@Component({
  selector: 'event-edit-location',
  templateUrl: './edit-location.component.html',
  styleUrls: ['./edit-location.component.css']
})
export class EditLocationComponent extends BaseViewComponent {
  @ViewChild("viewTemplate") viewTemplate: TemplateRef<any>;
  @ViewChild("editTemplate") editTemplate: TemplateRef<any>;

  @Input() set model(value: any) {
    if (value) {
      this.location = Object.assign({}, value);
      this.getCurrentAttendancesAtLocation().then(() => this.reset());
    }
  }
  @Input() isEditing = false;
  @Input() showInactive: boolean;

  @Output() modelChange = new EventEmitter<any>();

  private _mapLocations$: BehaviorSubject<EsriMarkerData[]> = new BehaviorSubject<EsriMarkerData[]>([]);
  get mapLocations$() {
    return this._mapLocations$.asObservable();
  }
  
  location: any = {};
  respondersAtLocation: number;
  formGroup: FormGroup;
  addressEntered: boolean = false;
  displayMap: boolean = false;

  constructor(
    private _responderCurrentAttendanceService: ResponderCurrentAttendanceService,
    private _geographyService: GeographyService,
    private _formBuilder: FormBuilder,
    private _modalService: BsModalService,
    private _postalCodePipe: PostalCodePipe,
    private _toastService: ToastService,
    lookupService: LookupService) {
    super(lookupService);

    this.formGroup = this._formBuilder.group({
      locationName: ["", Validators.required],
      streetAddress: "",
      streetAddress2: "",
      city: ["", Validators.required],
      stateNameId: ["", Validators.required],
      zipCode: "",
      latitude: "",
      longitude: "",
      showMap: false,
      isInternational: false,
      pointOfContact: "",
      phoneNumber: ["", Validators.pattern(/\([0-9]{3}\)\s?[0-9]{3}-[0-9]{4}/)],
      phoneExtension: ["", Validators.pattern(/^[0-9]{1,10}$/)],
      airportName: "",
      active: true
    });
  }

  reset() {
    this.formGroup.reset();
    this.formGroup.patchValue({
      locationName: this.location.name,
      streetAddress: this.location.address,
      streetAddress2: this.location.address2,
      city: this.location.city,
      stateNameId: this.location.stateNameId,
      zipCode: this.location.zipCode,
      latitude: this.location.latitude,
      longitude: this.location.longitude,
      showMap: this.location.showOnMap,
      pointOfContact: this.location.pointOfContact,
      phoneNumber: this.location.phoneNumber,
      phoneExtension: this.location.extension,
      airportName: this.location.airportName,
      active: this.location.active
    });
    this.addressEntered = this.formGroup.get("city").value !== "" && this.formGroup.get("stateNameId").value !== "";
    if (this.location.latitude && this.location.longitude) {
      let mapLocation = [
        {
          latitude: this.location.latitude,
          longitude: this.location.longitude,
          title: this.location.name,
          address: "",
          cityStateZip: "",
          enroute: null,
          onsite: null,
          staged: null,
          billeting: null,
          url: ""
        }
      ];
      this._mapLocations$.next(mapLocation);
      this.displayMap = true;
    }
  }

  commit() {
    this.location.name = this.formGroup.get("locationName").value;
    this.location.address = this.formGroup.get("streetAddress").value;
    this.location.address2 = this.formGroup.get("streetAddress2").value;
    this.location.city = this.formGroup.get("city").value;
    this.location.stateNameId = this.formGroup.get("stateNameId").value;
    this.location.zipCode = this._postalCodePipe.transform(this.formGroup.get("zipCode").value);
    this.location.latitude = this.formGroup.get("latitude").value;
    this.location.longitude = this.formGroup.get("longitude").value;
    this.location.showOnMap = this.formGroup.get("showMap").value;
    this.location.pointOfContact = this.formGroup.get("pointOfContact").value;
    this.location.phoneNumber = this.formGroup.get("phoneNumber").value;
    this.location.extension = this.formGroup.get("phoneExtension").value;
    this.location.airportName = this.formGroup.get("airportName").value;
    this.location.active = this.formGroup.get("active").value;
    this.location.showInactive = this.showInactive;
    this.modelChange.emit(this.location);
  }

  onSearchAirportClick(event?) {
    let activeElement = document.activeElement;
    let pointerType = event ? event.pointerType : '';
    this.lookupService.set("States", this.lookupService.get("LocationStates"));

    const modal = this._modalService.show(SearchAirportsModalComponent, { backdrop: "static", class: "gray modal-lg" });
    modal.content.selectedItem = this.location.airport;

    modal.onHide.pipe(first()).subscribe(() => {
      if (activeElement && activeElement instanceof HTMLElement) {
        activeElement.focus();
        if (pointerType == "mouse") activeElement.blur();
      }
    });
    this.safeSubscribe(modal.content.selection,
      async selectedAirport => {
        this.location.airport = selectedAirport;
        this.location.airportId = selectedAirport.id;
        this.formGroup.get("airportName").setValue(selectedAirport.airportName + " (" + selectedAirport.iataCode + ")");
        modal.hide();
      });
    this.safeSubscribe(modal.content.cancel, () => modal.hide());
  }

  onSearchMapClick() {
    let address: string = this.formGroup.get("streetAddress").value + ' ' +
      this.formGroup.get("streetAddress2").value + ' ' +
      this.formGroup.get("city").value + ' ' +
      this.formGroup.get("zipCode").value.slice(0, 5) + ' ' +
      this.getLookupValue("LocationStates", this.formGroup.get("stateNameId").value);
    this._geographyService.geocodeAddress(address).then((data) => {
      this.formGroup.get("latitude").setValue(data.latitude);
      this.formGroup.get("longitude").setValue(data.longitude);
      let mapLocation = [
        {
          latitude: data.latitude,
          longitude: data.longitude,
          title: this.formGroup.get("locationName").value,
          address: "",
          cityStateZip: "",
          enroute: null,
          onsite: null,
          staged: null,
          billeting: null,
          url: ""
        }
      ];
      this._mapLocations$.next(mapLocation);
      this.displayMap = true;
    });
  }

  onCityChange() {
    this.addressEntered = this.formGroup.get("city").value !== "" && this.formGroup.get("stateNameId").value !== "";
  }

  onStateChange() {
    this.addressEntered = this.formGroup.get("city").value !== "" && this.formGroup.get("stateNameId").value !== "";
    this.location.region = this.getRegion(this.lookupService.getLookupValue("LocationStates", this.formGroup.get("stateNameId").value).display);
  }

  private getCurrentAttendancesAtLocation() {
    let filter = Object.assign({}, {
      findKeys: {
        "EventId": this.location.eventId.toString(),
        "EventLocationId": this.location.id.toString()
      },
      includes: ["CurrentEventAttendance"],
      pagingInfo: {
        "pageNumber": 0,
        "pageSize": 10000
      }
    });
    return new Promise<void>((resolve, reject) => {
      this.safeSubscribe(this._responderCurrentAttendanceService.FindInDomain(filter), data => {
        this.respondersAtLocation = data.TotalRecordCount;
        return resolve();
      }, error => {
        this._toastService.showError(error);
        reject();
      });
    });
  }

  private getRegion(state: string): string {
    let region: string = "";

    switch (state) {
      case "Connecticut":
      case "Maine":
      case "Massachusetts":
      case "New Hampshire":
      case "Rhode Island":
      case "Vermont":
        region = "I";
        break;
      case "New Jersey":
      case "New York":
      case "Puerto Rico":
      case "Virgin Islands (U.S)":
        region = "II";
        break;
      case "Delaware":
      case "District of Columbia":
      case "Maryland":
      case "Pennsylvania":
      case "Virginia":
      case "West Virginia":
        region = "III";
        break;
      case "Alabama":
      case "Florida":
      case "Georgia":
      case "Kentucky":
      case "North Carolina":
      case "South Carolina":
      case "Tennessee":
        region = "IV";
        break;
      case "Illinois":
      case "Indiana":
      case "Michigan":
      case "Minnesota":
      case "Ohio":
      case "Wisconsin":
        region = "V";
        break;
      case "Arkansas":
      case "Louisiana":
      case "Mississippi":
      case "New Mexico":
      case "Oklahoma":
      case "Texas":
        region = "VI";
        break;
      case "Iowa":
      case "Kansas":
      case "Missouri":
      case "Nebraska":
        region = "VII";
        break;
      case "Colorado":
      case "Montana":
      case "North Dakota":
      case "South Dakota":
      case "Utah":
      case "Wyoming":
        region = "VIII";
        break;
      case "Arizona":
      case "American Samoa":
      case "California":
      case "Federated States of Micronesia":
      case "Guam":
      case "Hawaii":
      case "Marshall Islands":
      case "Nevada":
      case "Northern Mariana Islands":
      case "Palau Island":
        region = "IX";
        break;
      case "Alaska":
      case "Idaho":
      case "Oregon":
      case "Washington":
        region = "X";
        break;
    }
    return region;
  }

}
